// src/SchedulerScreen.jsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import dayjs from "dayjs";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear, faChevronLeft, faChevronRight, faClock, faGlobe, faCalendarDays, faCircleCheck, faStar } from '@fortawesome/free-solid-svg-icons';
import './ScheduleScreen.css';
import { faDollarSign } from '@fortawesome/free-solid-svg-icons/faDollarSign';

const useQuery = () => {
  const search = useLocation().search;
  // Replace bare '+' with '%2B' before parsing so URLSearchParams
  // doesn't decode them as spaces (important for phone numbers like +1...)
  return new URLSearchParams(search.replace(/\+/g, '%2B'));
};

// const LOGIN_URL = process.env.REACT_APP_INFLEXXIONHEALTH_LOGIN;
const RESET_PASSWORD_URL = process.env.REACT_APP_INFLEXXIONHEALTH_RESET_PASSWORD; 
const GA4_ID = process.env.REACT_APP_GA4_ID;

const STEPS = {
  SELECT: 'select',
  CONFIRM: 'confirm',
  SUCCESS: 'success',
  RETURNING: 'returning',
};

const stateInfo = {
  "CO": { full_state: "Colorado", timezone: "America/Denver", timezone_formatted: "Mountain Time" },
  "UT": { full_state: "Utah", timezone: "America/Denver", timezone_formatted: "Mountain Time" },
  "AZ": { full_state: "Arizona", timezone: "America/Phoenix", timezone_formatted: "Mountain Standard Time" },
  "WY": { full_state: "Wyoming", timezone: "America/Denver", timezone_formatted: "Mountain Standard Time" },
  "ID": { full_state: "Idaho", timezone: "America/Boise", timezone_formatted: "Mountain Time" },
};

const ScheduleScreen = ({ verifiedCopay }) => {
  const query = useQuery();

  const firstName = (query.get("first_name") || "").trim();
  const lastName = (query.get("last_name") || "").trim();
  const email = (query.get("email") || "").trim();
  const phone = (query.get("phone") || "").trim();
  const state = (query.get("state") || "").trim();
  const promoCode = (query.get("promo_code") || "").trim();
  const previousVisitorParam = query.get("ep") || false;

  const info = stateInfo[state] || {};
  const full_state = info.full_state || "";
  const timezone = info.timezone || "";
  const timezone_formatted = info.timezone_formatted || "";

  const [step, setStep] = useState(previousVisitorParam ? STEPS.RETURNING : STEPS.SELECT);
  const [scheduleData, setScheduleData] = useState({});
  const [appointmentPrice, setAppointmentPrice] = useState(null);
  const [promoApplied, setPromoApplied] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConfirming, setIsConfirming] = useState(false);
  const [passwordLink, setPasswordLink] = useState("");
  const [returningVisitor, setReturningVisitor] = useState(false);
  const [apiErrorModal, setApiErrorModal] = useState("");
  const [apiErrorTitle, setApiErrorTitle] = useState("");
  const [viewMonth, setViewMonth] = useState(dayjs().month());
  const [viewYear, setViewYear] = useState(dayjs().year());

  const supportEmail = process.env.REACT_APP_INFLEXXIONHEALTH_SUPPORT_EMAIL;

  const modalRef = useRef(null);
  const modalCloseRef = useRef(null);
  const modalTriggerRef = useRef(null);

  // Load all slots for the next 45 days
  useEffect(() => {
    const loadAllSlots = async () => {
      setIsLoading(true);
      const start = new Date();
      const end = new Date();
      end.setDate(start.getDate() + 45);

      try {
        const res = await fetch(`${process.env.REACT_APP_INFLEXXIONHEALTH_API_URL}/available-slots`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          mode: "cors",
          body: JSON.stringify({
            start_date: start.toISOString(),
            end_date: end.toISOString(),
            state,
            first_name: firstName,
            last_name: lastName,
            email,
            phone,
            promo_code: promoCode
          })
        });

        const data = await res.json();

        if (data.status === "error") {
          setApiErrorModal(data.message);
          setIsLoading(false);
          return;
        }

        setScheduleData(data.data?.slots || {});
        setAppointmentPrice(data.data?.appt_price || null);
        if (promoCode) setPromoApplied(data.data?.promo_applied ?? false);
      } catch (err) {
        console.error("Error loading slots:", err);
      }

      setIsLoading(false);
    };

    loadAllSlots();
  }, [firstName, lastName, email, phone, state, promoCode]);

  // Dates that have available slots
  const availableDates = useMemo(() => {
    const dates = new Set();
    const now = dayjs().startOf('day');
    Object.entries(scheduleData).forEach(([dateStr, slots]) => {
      if (dayjs(dateStr).isAfter(now) && slots.length > 0) {
        dates.add(dateStr);
      }
    });
    return dates;
  }, [scheduleData]);

  // Auto-select first available date when data loads
  useEffect(() => {
    if (!selectedDate && availableDates.size > 0) {
      const sorted = Array.from(availableDates).sort();
      setSelectedDate(sorted[0]);
      const d = dayjs(sorted[0]);
      setViewMonth(d.month());
      setViewYear(d.year());
    }
  }, [availableDates, selectedDate]);

  // Modal focus management: trap focus, return focus on close, Escape closes
  useEffect(() => {
    if (!apiErrorModal) return;

    modalTriggerRef.current = document.activeElement;
    if (modalCloseRef.current) modalCloseRef.current.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setApiErrorModal("");
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusables = modalRef.current.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const trigger = modalTriggerRef.current;
      if (trigger && typeof trigger.focus === 'function') trigger.focus();
    };
  }, [apiErrorModal]);

  // Calendar helpers
  const daysInMonth = dayjs().year(viewYear).month(viewMonth).daysInMonth();
  const firstDayOfMonth = dayjs().year(viewYear).month(viewMonth).date(1).day();
  const monthLabel = dayjs().year(viewYear).month(viewMonth).format("MMMM YYYY");

  const today = dayjs().startOf('day');
  const maxDate = dayjs().add(45, 'day');

  const canGoPrev = () => {
    const prevMonth = dayjs().year(viewYear).month(viewMonth).subtract(1, 'month');
    return prevMonth.endOf('month').isAfter(today) || prevMonth.endOf('month').isSame(today, 'day');
  };

  const canGoNext = () => {
    const nextMonth = dayjs().year(viewYear).month(viewMonth).add(1, 'month');
    return nextMonth.startOf('month').isBefore(maxDate);
  };

  const navigateMonth = (direction) => {
    const current = dayjs().year(viewYear).month(viewMonth);
    const next = direction === 'next' ? current.add(1, 'month') : current.subtract(1, 'month');
    setViewMonth(next.month());
    setViewYear(next.year());
  };

  const isDateAvailable = (day) => {
    const dateStr = dayjs().year(viewYear).month(viewMonth).date(day).format('YYYY-MM-DD');
    return availableDates.has(dateStr);
  };

  const isDateSelected = (day) => {
    if (!selectedDate) return false;
    const dateStr = dayjs().year(viewYear).month(viewMonth).date(day).format('YYYY-MM-DD');
    return dateStr === selectedDate;
  };

  const isToday = (day) => {
    const dateStr = dayjs().year(viewYear).month(viewMonth).date(day).format('YYYY-MM-DD');
    return dateStr === today.format('YYYY-MM-DD');
  };

  const handleDateClick = useCallback((day) => {
    const dateStr = dayjs().year(viewYear).month(viewMonth).date(day).format('YYYY-MM-DD');
    if (availableDates.has(dateStr)) {
      setSelectedDate(dateStr);
      setSelectedTime(null);
      setSelectedSlot(null);
    }
  }, [viewYear, viewMonth, availableDates]);

  const handleTimeClick = (time, provider_id, raw_date) => {
    setSelectedTime(time);
    setSelectedSlot({
      time,
      provider_id,
      apptDate: selectedDate,
      firstName,
      lastName,
      phone,
      email,
      timezone,
      raw_date,
      promoCode
    });
  };

  const handleNext = () => {
    if (selectedSlot) {
      setStep(STEPS.CONFIRM);
    }
  };

  const handleBack = () => {
    setIsConfirming(false);
    setStep(STEPS.SELECT);
  };

  const sendGaEvent = (eventLabel) => {
    if (GA4_ID && GA4_ID !== 'IGNORE' && navigator.sendBeacon) {
      const gaCookie = document.cookie.split('; ').find(r => r.startsWith('_ga='));
      const clientId = gaCookie ? gaCookie.split('=')[1].split('.').slice(2).join('.') : 'unknown';

      navigator.sendBeacon('https://www.google-analytics.com/g/collect', new URLSearchParams({
        v: '2',
        tid: GA4_ID,
        cid: clientId,
        en: 'Clicked',
        'ep.event_category': 'User Interaction',
        'ep.event_label': eventLabel,
      }));
    }
  };

  const redirectToHealthie = (eventName) => {
    const destination = passwordLink || RESET_PASSWORD_URL;
    sendGaEvent(eventName);
    window.location.href = destination;
  };

  const confirmBooking = async () => {
    if (isConfirming) return;
    setIsConfirming(true);
    sendGaEvent('Confirm Appt');

    const { provider_id, raw_date } = selectedSlot;

    try {
      const res = await fetch(`${process.env.REACT_APP_INFLEXXIONHEALTH_API_URL}/complete-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        mode: "cors",
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          phone,
          email,
          state,
          timezone,
          provider_id,
          raw_date,
          promo_code: promoCode
        })
      });

      const data = await res.json();

      if (!res.ok) {
        if ((res.status === 400 || res.status === 500) && data?.message === "Already registered") {
          setStep(STEPS.RETURNING);
          return;
        }
        setApiErrorTitle(data?.title || "");
        setApiErrorModal(data?.message);
        setStep(STEPS.SELECT);
        return;
      }

      if (data?.status === "success") {
        setPasswordLink(data.set_password_link);
        setReturningVisitor(Boolean(data.returningVisitor));
        setStep(STEPS.SUCCESS);
      } else {
        setApiErrorTitle(data?.title || "");
        setApiErrorModal(data?.message);
        setStep(STEPS.SELECT);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setIsConfirming(false);
    }
  };

  const slotsForSelectedDate = selectedDate ? (scheduleData[selectedDate] || []) : [];
  const selectedDateFormatted = selectedDate ? dayjs(selectedDate).format("dddd, MMMM D") : "";
  const timezoneAbbr = timezone.includes("Denver") ? "MT" : "MST";

  // Build calendar grid
  const calendarCells = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarCells.push(
      <div key={`empty-${i}`} className="cal-cell cal-empty" role="presentation" />
    );
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const available = isDateAvailable(day);
    const selected = isDateSelected(day);
    const todayClass = isToday(day);
    const dateObj = dayjs().year(viewYear).month(viewMonth).date(day);
    const dateLabel = dateObj.format("dddd, MMMM D, YYYY");

    let className = "cal-cell cal-day";
    if (selected) className += " cal-selected";
    else if (available) className += " cal-available";
    else className += " cal-unavailable";
    if (todayClass) className += " cal-today";

    const ariaLabel = available
      ? `${dateLabel}${selected ? ', selected' : ''}, available`
      : `${dateLabel}, no appointments available`;

    calendarCells.push(
      <button
        type="button"
        key={day}
        className={className}
        onClick={available ? () => handleDateClick(day) : undefined}
        disabled={!available}
        aria-pressed={available ? selected : undefined}
        aria-label={ariaLabel}
      >
        <span aria-hidden="true">{day}</span>
      </button>
    );
  }

  return (
    <div className="calendly-widget">
      {apiErrorModal && (
        <div
          className="cal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setApiErrorModal("");
              setApiErrorTitle("");
            }
          }}
        >
          <div
            className="cal-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="error-modal-title"
            aria-describedby="error-modal-body"
            ref={modalRef}
          >
            <h2 id="error-modal-title" style={{ marginBottom: "10px" }}>{apiErrorTitle || "No Appointments Available"}</h2>
            <p id="error-modal-body" style={{ color: "#b71c1c", marginBottom: "20px" }}>
              {apiErrorModal}
            </p>
            <button
              type="button"
              className="cal-btn cal-btn-primary"
              onClick={() => {
                setApiErrorModal("");
                setApiErrorTitle("");
              }}
              ref={modalCloseRef}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Step 1: Select Date & Time */}
      {step === STEPS.SELECT && (
        <section className="cal-container" aria-labelledby="select-heading">
          <div className="cal-sidebar">
            <div className="cal-meeting-info">
              <p className="cal-duration">
                <FontAwesomeIcon icon={faClock} className="cal-info-icon" aria-hidden="true" />
                30 minute Initial Consultation
              </p>
              <p className="cal-timezone">
                <FontAwesomeIcon icon={faGlobe} className="cal-info-icon" aria-hidden="true" />
                {timezone_formatted} - {full_state}
              </p>

              {verifiedCopay ? (
                <p className="cal-price">
                  <FontAwesomeIcon icon={faDollarSign} className="cal-info-icon" aria-hidden="true" />
                  You'll have a ${verifiedCopay} co-pay at the time of the appointment (pending verification).
                </p>
              ) : appointmentPrice && (
                <p className="cal-price">
                  <FontAwesomeIcon icon={faDollarSign} className="cal-info-icon" aria-hidden="true" />
                  You'll be billed {appointmentPrice} after your appointment.
                </p>
              )}
              {promoCode && !isLoading && (
                <p className="cal-price">
                  <FontAwesomeIcon icon={faStar} className="cal-info-icon" aria-hidden="true" />
                  {promoApplied ? `Code ${promoCode} applied.` : `Code ${promoCode} was invalid or expired.`}
                </p>
              )}
            </div>
          </div>

          <div className="cal-divider" aria-hidden="true" />

          <div className="cal-main">
            <h2 id="select-heading" className="cal-heading">Select a Date &amp; Time</h2>

            <div className="cal-date-time-layout">
              <div className="cal-calendar-section">
                <div className="cal-month-nav">
                  <button
                    type="button"
                    className="cal-nav-btn"
                    onClick={() => navigateMonth('prev')}
                    disabled={!canGoPrev()}
                    aria-label="Previous month"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
                  </button>
                  <span className="cal-month-label" aria-live="polite" aria-atomic="true">
                    {monthLabel}
                  </span>
                  <button
                    type="button"
                    className="cal-nav-btn"
                    onClick={() => navigateMonth('next')}
                    disabled={!canGoNext()}
                    aria-label="Next month"
                  >
                    <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
                  </button>
                </div>

                <div
                  className="cal-grid"
                  role="grid"
                  aria-label={`${monthLabel} appointment availability`}
                >
                  {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(d => (
                    <div key={d} className="cal-cell cal-header" role="columnheader">{d}</div>
                  ))}
                  {calendarCells}
                </div>
              </div>

              <div className="cal-times-section" aria-labelledby="times-heading">
                {isLoading ? (
                  <div className="cal-loading" role="status" aria-live="polite">
                    <FontAwesomeIcon icon={faGear} className="fa-spin" aria-hidden="true" />
                    <span>Loading available times...</span>
                  </div>
                ) : selectedDate ? (
                  <>
                    <h3 id="times-heading" className="cal-times-heading">{selectedDateFormatted}</h3>
                    <div className="cal-times-list">
                      {slotsForSelectedDate.length > 0 ? (
                        slotsForSelectedDate.map(({ time, provider_id, raw_date }) => (
                          <div key={`${selectedDate}-${time}`} className="cal-time-row">
                            <button
                              type="button"
                              className={`cal-time-btn ${selectedTime === time ? 'cal-time-selected' : ''}`}
                              onClick={() => handleTimeClick(time, provider_id, raw_date)}
                              aria-label={`${time} on ${selectedDateFormatted}`}
                              aria-pressed={selectedTime === time}
                            >
                              {time}
                            </button>
                            {selectedTime === time && (
                              <button
                                type="button"
                                className="cal-next-btn"
                                onClick={handleNext}
                                aria-label={`Continue with ${time} on ${selectedDateFormatted}`}
                              >
                                Next
                              </button>
                            )}
                          </div>
                        ))
                      ) : (
                        <p className="cal-no-times">No available times for this date.</p>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="cal-no-times">Select a date to view available times.</p>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Step 2: Confirm */}
      {step === STEPS.CONFIRM && selectedSlot && (
        <section className="cal-container cal-container-centered" aria-labelledby="confirm-heading">
          <div className="cal-confirm-panel">
            <div className="cal-header-block">
              <button
                type="button"
                className="cal-back-arrow"
                onClick={handleBack}
                aria-label="Back to date selection"
              >
                <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
              </button>
              <h2 id="confirm-heading" className="cal-heading-no-mb">Confirm Your Appointment</h2>
            </div>

            <div className="cal-confirm-details">
              <div className="cal-confirm-row">
                <FontAwesomeIcon icon={faClock} className="cal-confirm-icon" aria-hidden="true" />
                <span>30 minute Initial Consultation</span>
              </div>
              <div className="cal-confirm-row">
                <FontAwesomeIcon icon={faCalendarDays} className="cal-confirm-icon" aria-hidden="true" />
                <span>{selectedDateFormatted}</span>
              </div>
              <div className="cal-confirm-row">
                <FontAwesomeIcon icon={faClock} className="cal-confirm-icon" aria-hidden="true" />
                <span>{selectedSlot.time} {timezoneAbbr}</span>
              </div>
            </div>

            <p className="cal-confirm-note">
              Click below to schedule. You will be redirected to create a password in
              our patient portal and complete your forms.
            </p>

            <div className="cal-confirm-actions">
              <button
                type="button"
                className="cal-btn cal-btn-primary cal-btn-wide"
                onClick={confirmBooking}
                disabled={isConfirming}
                aria-busy={isConfirming}
              >
                {isConfirming ? (
                  <>
                    <span>Scheduling...</span>
                    <FontAwesomeIcon
                      icon={faGear}
                      className="fa-spin"
                      style={{ marginLeft: '8px' }}
                      aria-hidden="true"
                    />
                  </>
                ) : (
                  "Schedule Appointment"
                )}
              </button>
            </div>

            <p className="cal-help-text">
              For help scheduling email&nbsp;
              <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
            </p>
          </div>
        </section>
      )}

      {/* Step 3a: Success */}
      {step === STEPS.SUCCESS && selectedSlot && (
        <section className="cal-container cal-container-centered" aria-labelledby="success-heading">
          <div className="cal-success-panel">
            <FontAwesomeIcon icon={faCircleCheck} className="cal-success-icon" aria-hidden="true" />
            <h2 id="success-heading" className="cal-success-heading">You are scheduled</h2>
            <p className="cal-success-sub" role="status">
              We'll see you on {selectedDateFormatted} at {selectedSlot.time} {timezoneAbbr}
            </p>

            <div className="cal-success-card">
              <h3>30 Minute Initial Consultation</h3>
              <div className="cal-confirm-row">
                <FontAwesomeIcon icon={faCalendarDays} className="cal-confirm-icon" aria-hidden="true" />
                <span>{selectedSlot.time} - {selectedDateFormatted}</span>
              </div>
              <div className="cal-confirm-row">
                <FontAwesomeIcon icon={faGlobe} className="cal-confirm-icon" aria-hidden="true" />
                <span>{timezone_formatted} - {full_state}</span>
              </div>
            </div>

            <div className="cal-success-next">
              <h3>What's next?</h3>
              <ol style={{ marginLeft: '-1.5em' }}>
                {returningVisitor && (
                  <>
                    {/* Forcing users to reset their password at this point because we can't customize the login page and the IH specific reset password page fails silently for returning visitors. */
                    <li>You already have an account on <strong>gethealthie.com</strong>, our secure patient portal. Enter your email address on the next page to reset your password.</li>
                    }
                  </>
                )}

                {!returningVisitor && (
                  <>
                    <li>We'll bring you over to <strong>gethealthie.com</strong>, our secure patient portal. Create a password to log in.</li>                    
                  </>
                )}

                <li>Put in your payment information. You won't be billed until <strong><em>after</em></strong> your initial consultation.</li>               
                <li>Fill out your intake forms — it'll take just a few minutes.</li>
              </ol>

              {promoCode && (
                  <p><em>Your promo code has been saved, it will be applied when you are eventually charged.</em></p>
                )}
            </div>

            <button
              type="button"
              className="cal-btn cal-btn-primary cal-btn-wide"
              onClick={() => redirectToHealthie('go_to_healthie')}
            >
              Let's go!
            </button>

            <p className="cal-help-text">
              Need help? Email&nbsp;
              <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
            </p>
          </div>
        </section>
      )}
    </div>
  );
};

export default ScheduleScreen;
